import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useQueryUser } from '@heidi/workspace/data';
import { useCallback, useEffect, useState } from 'react';
import { z, ZodError } from 'zod';
import { useShareUserDocumentByEmail } from './useShareUserDocumentByEmail';

const emailSchema = z.string().email();

/**
 * Encapsulates email dialog state and handlers for document items.
 * Reused across ItemCard, DocumentTable, CanvasPanel, and DocumentPreviewModal.
 */
export function useDocumentEmail(documentId: string | null) {
  const { t } = useTranslations();
  const { data: user } = useQueryUser();
  const {
    mutateAsync: sendEmail,
    isPending,
    reset,
  } = useShareUserDocumentByEmail(documentId);

  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail(user?.default_export_email ?? user?.email ?? '');
    }
  }, [isOpen, user?.default_export_email, user?.email]);

  const openEmailDialog = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    setIsOpen(false);
  }, [reset]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose]
  );

  const handleSend = useCallback(async () => {
    try {
      emailSchema.parse(email);
      await sendEmail(email);
      handleClose();
    } catch (error) {
      if (error instanceof ZodError) {
        toast({
          tone: 'destructive',
          title: t('sendAsEmailModal.error'),
          description: t('sendAsEmailModal.invalidEmail'),
        });
      }
    }
  }, [email, sendEmail, handleClose, t]);

  return {
    isOpen,
    email,
    isPending,
    setEmail,
    openEmailDialog,
    handleSend,
    onOpenChange,
  };
}
